/**
 * StockDataPlanFormDialog - Cadastro/edição de plano de dados dos chips
 *
 * Mesmo padrão visual dos cadastros de Pessoas/Endereço (cabeçalho e rodapé
 * fixos, seções, tela cheia no celular). Validação definitiva na RPC
 * `upsert_stock_data_plan` (operadoras em allowlist, fornecedor do tenant).
 *
 * RELEASE: 2026-10-08 v1.1.0 - Pacote em MB (inteiro) e callback onSuccess (atalho do chip).
 * RELEASE: 2026-10-07 v1.0.0 - Versão inicial (Gestão de Chip).
 */
import { useEffect, useState } from 'react';
import { Coins, Loader2, Plus, Signal, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FormSection, FieldLabel } from '@/components/common/FormSection';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import {
  DATA_PLAN_CARRIERS, useStockRegistryMutations, type DataPlanCarrier, type StockDataPlan,
} from '@/hooks/useStockRegistries';

/** Campos numéricos digitados como texto (aceita vírgula decimal). */
const NUM_FIELDS = [
  'loyalty_months', 'grace_days', 'original_amount', 'negotiated_amount', 'due_day', 'closing_day',
  'penalty_per_chip', 'overage_mb_amount', 'overage_gb_amount', 'sms_amount',
] as const;
type NumField = (typeof NUM_FIELDS)[number];

const onlyInt = (v: string, max = 4) => v.replace(/\D/g, '').slice(0, max);
const onlyDecimal = (v: string, decimals: number) => {
  const clean = v.replace(/[^\d,]/g, '');
  const [int, ...rest] = clean.split(',');
  return rest.length ? `${int.slice(0, 9)},${rest.join('').slice(0, decimals)}` : int.slice(0, 9);
};
const toText = (n: number | null | undefined, decimals?: number) =>
  n === null || n === undefined ? '' : decimals ? n.toFixed(decimals).replace('.', ',') : String(n);
const toNumber = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')));

interface Props {
  open: boolean;
  plan: StockDataPlan | null;
  onOpenChange: (open: boolean) => void;
  /** Chamado após salvar com id e nome do plano. */
  onSuccess?: (id: string, name: string) => void;
}

export function StockDataPlanFormDialog({ open, plan, onOpenChange, onSuccess }: Props) {
  const isMobile = useIsMobile();
  const { canCreate } = usePermissions();
  const { upsertDataPlan } = useStockRegistryMutations();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [supplier, setSupplier] = useState<string | null>(null);
  const [supplierLabel, setSupplierLabel] = useState<string | null>(null);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [dataPackage, setDataPackage] = useState('');
  const [carriers, setCarriers] = useState<DataPlanCarrier[]>([]);
  const [carrierError, setCarrierError] = useState(false);
  const [nums, setNums] = useState<Record<NumField, string>>({} as Record<NumField, string>);
  const [notes, setNotes] = useState('');

  // Carrega o plano ao abrir (ou limpa para um novo)
  useEffect(() => {
    if (!open) return;
    setName(plan?.name ?? '');
    setNameError(null);
    setCarrierError(false);
    setSupplier(plan?.supplier_person_id ?? null);
    setSupplierLabel(plan?.supplier_name ?? null);
    setDataPackage(toText(plan?.data_package_mb));
    setCarriers(plan?.compatible_carriers ?? []);
    setNotes(plan?.notes ?? '');
    setNums({
      loyalty_months: toText(plan?.loyalty_months), grace_days: toText(plan?.grace_days),
      original_amount: toText(plan?.original_amount, 2), negotiated_amount: toText(plan?.negotiated_amount, 2),
      due_day: toText(plan?.due_day), closing_day: toText(plan?.closing_day),
      penalty_per_chip: toText(plan?.penalty_per_chip, 2), overage_mb_amount: toText(plan?.overage_mb_amount, 3),
      overage_gb_amount: toText(plan?.overage_gb_amount, 2), sms_amount: toText(plan?.sms_amount, 2),
    });
  }, [open, plan]);

  const setNum = (k: NumField, v: string) => setNums((p) => ({ ...p, [k]: v }));
  const toggleCarrier = (c: DataPlanCarrier, checked: boolean) => {
    setCarrierError(false);
    setCarriers((cur) => (checked ? [...cur, c] : cur.filter((x) => x !== c)));
  };

  const save = () => {
    let invalid = false;
    if (!name.trim()) { setNameError('Nome do plano é obrigatório'); invalid = true; }
    if (carriers.length === 0) { setCarrierError(true); invalid = true; }
    for (const k of ['due_day', 'closing_day'] as const) {
      const n = toNumber(nums[k] ?? '');
      if (n !== null && (n < 1 || n > 31)) { toast.error('Dias de vencimento e fechamento devem estar entre 1 e 31'); invalid = true; }
    }
    if (invalid) return;
    const data: Record<string, unknown> = {
      name: name.trim(), supplier_person_id: supplier, data_package_mb: toNumber(dataPackage),
      compatible_carriers: carriers, notes, is_active: plan?.is_active ?? true,
    };
    NUM_FIELDS.forEach((k) => { data[k] = toNumber(nums[k] ?? ''); });
    upsertDataPlan.mutate({ id: plan?.id ?? null, data }, { onSuccess: (id) => { onSuccess?.(id, name.trim()); onOpenChange(false); } });
  };

  const inputCls = 'h-9 border-border/50';
  const numInput = (k: NumField, placeholder: string, mode: 'int' | 2 | 3) => (
    <Input className={inputCls} inputMode={mode === 'int' ? 'numeric' : 'decimal'} placeholder={placeholder}
      value={nums[k] ?? ''}
      onChange={(e) => setNum(k, mode === 'int' ? onlyInt(e.target.value, k.endsWith('_day') ? 2 : 4) : onlyDecimal(e.target.value, mode))} />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('flex flex-col gap-0 overflow-hidden p-0', isMobile ? 'h-[100dvh] w-screen max-w-full rounded-none' : 'max-h-[90vh] max-w-4xl')}>
        <DialogHeader className="shrink-0 border-b p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5">
              {upsertDataPlan.isPending ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Signal className="h-5 w-5 text-primary" />}
            </div>
            <div className="text-left">
              <DialogTitle className="text-lg">{plan ? 'Editar plano de dados' : 'Novo plano de dados'}</DialogTitle>
              <DialogDescription className="text-sm">Condições contratadas que serão vinculadas aos chips.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
            <FormSection icon={Signal} title="Identificação">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <FieldLabel required>Nome do plano</FieldLabel>
                  <Input className={inputCls} value={name} maxLength={120} placeholder="Ex.: M2M 10GB Vivo"
                    aria-invalid={!!nameError} onChange={(e) => { setName(e.target.value); setNameError(null); }} />
                  {nameError && <p className="text-[0.8rem] font-medium text-destructive">{nameError}</p>}
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Fornecedor</FieldLabel>
                  <div className="flex gap-2">
                    <PersonSelector value={supplier} role="supplier" placeholder="Sem fornecedor" selectedLabel={supplierLabel}
                      onSelect={(id) => { setSupplier(id); if (!id) setSupplierLabel(null); }} />
                    {canCreate('persons_suppliers') && (
                      <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setShowSupplierForm(true)}
                        aria-label="Atalho: cadastrar novo fornecedor" title="Atalho: cadastrar novo fornecedor">
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <FieldLabel>Pacote (MB)</FieldLabel>
                  <Input className={inputCls} value={dataPackage} inputMode="numeric" placeholder="Ex.: 20" onChange={(e) => setDataPackage(onlyInt(e.target.value, 9))} />
                </div>
                <div className="space-y-1.5"><FieldLabel>Fidelidade (meses)</FieldLabel>{numInput('loyalty_months', 'Ex.: 12', 'int')}</div>
                <div className="space-y-1.5"><FieldLabel>Carência (dias)</FieldLabel>{numInput('grace_days', 'Ex.: 30', 'int')}</div>
              </div>
              <div className="space-y-2">
                <FieldLabel required>Operadoras compatíveis</FieldLabel>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {DATA_PLAN_CARRIERS.map((c) => (
                    <label key={c.value} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox checked={carriers.includes(c.value)} onCheckedChange={(v) => toggleCarrier(c.value, v === true)} />
                      {c.label}
                    </label>
                  ))}
                </div>
                {carrierError && <p className="text-[0.8rem] font-medium text-destructive">Selecione ao menos uma operadora</p>}
              </div>
            </FormSection>

            <Separator />

            <FormSection icon={Coins} title="Valores e faturamento">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><FieldLabel>Valor original</FieldLabel>{numInput('original_amount', '0,00', 2)}</div>
                <div className="space-y-1.5"><FieldLabel>Valor negociado</FieldLabel>{numInput('negotiated_amount', '0,00', 2)}</div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1.5"><FieldLabel>Dia vencimento</FieldLabel>{numInput('due_day', 'Ex.: 5', 'int')}</div>
                <div className="space-y-1.5"><FieldLabel>Dia fechamento</FieldLabel>{numInput('closing_day', 'Ex.: 30', 'int')}</div>
                <div className="space-y-1.5"><FieldLabel>Multa por chip</FieldLabel>{numInput('penalty_per_chip', '0,00', 2)}</div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1.5"><FieldLabel>Valor excedente (MB)</FieldLabel>{numInput('overage_mb_amount', '0,000', 3)}</div>
                <div className="space-y-1.5"><FieldLabel>Valor excedente (GB)</FieldLabel>{numInput('overage_gb_amount', '0,00', 2)}</div>
                <div className="space-y-1.5"><FieldLabel>Valor SMS</FieldLabel>{numInput('sms_amount', '0,00', 2)}</div>
              </div>
            </FormSection>

            <Separator />

            <FormSection icon={StickyNote} title="Observações" hint="(opcional)">
              <Textarea className="resize-none border-border/50" value={notes} maxLength={1000} rows={3}
                placeholder="Informações adicionais sobre o plano..." onChange={(e) => setNotes(e.target.value)} />
            </FormSection>
          </div>
        </ScrollArea>

        <DialogFooter className="shrink-0 gap-2 border-t p-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" onClick={save} disabled={upsertDataPlan.isPending}>
            {upsertDataPlan.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {plan ? 'Salvar' : 'Criar plano'}
          </Button>
        </DialogFooter>
      </DialogContent>
      <PersonFormDialog open={showSupplierForm} onOpenChange={setShowSupplierForm} person={null} defaultRole="supplier"
        onOpenExisting={() => undefined}
        onSuccess={(id, label) => { setSupplier(id); setSupplierLabel(label); setShowSupplierForm(false); }} />
    </Dialog>
  );
}
